import * as I from "immutable";
import moment from "moment-timezone";
import uuid from "node-uuid";
import Raven from "raven-js";
import React, {Component} from "react";
import PropTypes from "prop-types";

import AutoTextarea from "components/shared/AutoTextarea";
import Markdown from "components/shared/Markdown";

import {TIME_FORMAT} from "config/constants";
import {trackEvent} from "metrics";
import {formatDuration, isCtrlPressed, isDesktop, isMac} from "utils";

import api from "v2/api";
import {getDestination} from "v2/api/Destinations";
import {AppData, Route, Task} from "v2/api/Records";
import {canReorderDestinations} from "v2/api/Routes";

import DestinationAddress from "v2/components/DestinationAddress";
import DestinationTitle from "v2/components/DestinationTitle";
import ReorderTasks from "v2/components/dnd/ReorderTasks";
import ModalForm from "v2/components/responsive/ModalForm";

import styles from "v2/styles";

export default class EditDestinationForm extends Component {
  static contextTypes = {
    appData: PropTypes.instanceOf(AppData),
    location: PropTypes.object,
    onUpdateRouteData: PropTypes.func,
    params: PropTypes.shape({
      date: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired,
      routeId: PropTypes.string.isRequired,
    }),
    routeData: PropTypes.instanceOf(Route),
    router: PropTypes.object,
  }
  static defaultProps = {
    crmLinkStyle: {
      fontSize: styles.FONT_SIZE.SMALL,
      fontWeight: "normal",
    },
  }
  static propTypes = {
    crmLinkStyle: PropTypes.object,
    modal: PropTypes.bool,
    onCancel: PropTypes.func,
  }

  constructor(props, context) {
    super(props, context);

    const {params: {id}, routeData} = context;
    const destination = routeData && getDestination(routeData, id);

    this.state = {
      duration: destination.duration,
      errors: I.Map(),
      estStartTime: destination.estStartTime,
      importantNotes: destination.importantNotes,
      notes: destination.notes,
      reorderTasks: false,
      saved: false,
      schedulerNotes: destination.schedulerNotes,
      submitted: false,
      tasks: destination.tasks,
    };
  }

  componentDidMount() {
    const {modal} = this.props;
    if (modal) {
      window.addEventListener("keydown", this.handleCtrlSubmit);
    }
  }

  componentWillUnmount() {
    const {modal} = this.props;
    if (modal) {
      window.addEventListener("keydown", this.handleCtrlSubmit);
    }
  }

  handleAddTask = (evt) => {
    evt.preventDefault();
    this.setState(({tasks}) => ({
      tasks: tasks.push(Task({
        id: uuid.v4(),
      })),
    }));
  }

  handleChangeDuration = (evt) => {
    this.setState({
      duration: moment.duration(parseInt(evt.target.value, 10), "minutes"),
    });
  }

  handleChangeEstStartTime = (unit, evt) => {
    const value = parseInt(evt.target.value, 10);
    this.setState(({estStartTime}) => ({
      estStartTime: estStartTime.clone().set(unit, value),
    }));
  }

  handleChangeTask = (id, key, evt) => {
    const value = evt.target.value;
    this.setState(({tasks}) => {
      const task = tasks.find(item => item.id === id);
      if (!task) {
        return {};
      }
      return {
        tasks: tasks.set(tasks.indexOf(task), task.set(key, value)),
      };
    });
  }

  handleChangeText = (key, evt) => {
    this.setState({
      [key]: evt.target.value,
    });
  }

  handleClickSlot = (evt) => {
    evt.preventDefault();

    const {location: {pathname, query}, router} = this.context;
    const {onCancel} = this.props;

    onCancel(null, () => router.push({
      pathname,
      query: {
        ...query,
        modal: "reorder-destinations",
      },
    }));
  }

  handleCtrlSubmit = (evt) => {
    if (isCtrlPressed(evt) && evt.keyCode === 83) {
      this.handleSubmit(evt);
    }
  }

  handleDeleteTask = (id, evt) => {
    evt.preventDefault();
    this.setState(({tasks}) => {
      const task = tasks.find(item => item.id === id);
      if (!task) {
        return {};
      }
      return {
        tasks: tasks.delete(tasks.indexOf(task)),
      };
    });
  }

  handleDisableReorderTasks = (evt) => {
    evt.preventDefault();
    this.setState({
      reorderTasks: false,
    });
  }

  handleEnableReorderTasks = (evt) => {
    evt.preventDefault();
    this.setState({
      reorderTasks: true,
    });
  }

  handleExtraSubmit = (evt) => {
    return this.handleSubmit(evt, true);
  }

  handleMoveTask = (id, afterId) => {
    this.setState(({tasks}) => {
      const task = tasks.find(item => item.id === id);
      const afterTask = tasks.find(item => item.id === afterId);

      if (!task || !afterTask) {
        return {};
      }

      return {
        tasks: tasks
          .splice(tasks.indexOf(task), 1)
          .splice(tasks.indexOf(afterTask), 0, task),
      };
    });
  }

  handleSubmit = (evt, close=false) => {
    evt.preventDefault();
    this.setState({
      submitted: true,
    });

    const {
      appData: {destinationType},
      location: {query},
      onUpdateRouteData,
      params: {date, id, routeId},
      routeData,
      router,
    } = this.context;
    const {modal, onCancel} = this.props;
    const {
      duration,
      estStartTime,
      importantNotes,
      notes,
      schedulerNotes,
    } = this.state;

    const data = {};
    const destination = getDestination(routeData, id);
    if (destination.slot === 1) {
      data["estimated_start_time"] = estStartTime.toISOString();
    }

    api()
      .patch(this.apiUrl, {
        ...data,
        duration: formatDuration(duration),
        notes,
        "notes_scheduler": schedulerNotes,
        warnings: importantNotes,
      })
      .then(() => {
        this.handleSubmitTasks(() => {
          onUpdateRouteData(null, () => {
            if (modal && close) {
              onCancel();
            }

            trackEvent("Edit Destination", ["Destination", "edit", id], {
              Date: date,
              ID: id,
              "Route ID": routeId,
              Type: destinationType.get(destination.type),
            });

            if (!modal || close) {
              router.push({
                pathname: `/${date}/${routeId}/${id}`,
                query,
              });
            } else {
              const additionalState = {};
              if (modal) {
                additionalState.saved = true;

                setTimeout(() => {
                  this.setState({
                    saved: false,
                  });
                }, 2500);
              }

              this.setState({
                submitted: false,
                ...additionalState,
              });
            }
          });
        });
      })
      .catch((err) => {
        if (err.response) {
          this.setState({
            errors: I.Map(err.response.data),
            submitted: false,
          });
        } else {
          Raven.captureException(err);
        }
      });
  }

  handleSubmitTasks = (onDone) => {
    const {
      appData: {destinationType},
      params: {date, id, routeId},
      routeData,
    } = this.context;
    const {tasks} = this.state;
    const destination = getDestination(routeData, id);

    if (!destination || I.is(destination.tasks, tasks)) {
      return onDone();
    }

    const apiUrl = `/routes/${routeId}/destinations/${id}/update-tasks`;

    const toAdd = tasks
      .filter(item => typeof(item.id) === "string")
      .map(item => ({
        uid: item.id,
        title: item.title || "",
        description: item.description || "",
      }))
      .toArray();
    const toDelete = destination
      .tasks
      .filter(oldItem => {
        return !tasks.find(item => item.id === oldItem.id);
      })
      .map(item => item.id)
      .toArray();
    const toUpdate = tasks
      .filter(item => {
        const oldTask = destination.tasks.find(oldItem => oldItem.id === item.id);
        return oldTask && !I.is(item, oldTask);
      })
      .map(item => ({
        id: item.id,
        title: item.title || "",
        description: item.description || "",
      }))
      .toArray();

    api()
      .patch(apiUrl, {
        create: toAdd,
        delete: toDelete,
        order: tasks.map(item => item.id).toArray(),
        update: toUpdate,
      })
      .then(() => {
        trackEvent(
          "Edit Destination Tasks",
          ["Destination", "edit-tasks", id],
          {
            Date: date,
            ID: id,
            "Route ID": routeId,
            "Route Type": routeData.type,
            Type: destinationType.get(destination.type),
          });
        onDone();
      })
      .then((err) => {
        if (err.response) {
          this.setState(({errors}) => ({
            errors: errors.merge(I.Map(err.response.data)),
            submitted: false,
          }));
        } else {
          Raven.captureException(err);
        }
      });
  }

  get apiUrl() {
    const {params: {id, routeId}} = this.context;
    return `/routes/${routeId}/destinations/${id}`;
  }

  getCRMLinks(destination) {
    if (!destination.house) {
      return [null, null];
    }

    const {crmLinkStyle} = this.props;
    const {id: houseId} = destination.house;
    const crmUrl = process.env.CRM_URL;

    const linkClassName = "pull-right";
    const linkLabel = "Edit in CRM »";

    const visitTemplatesUrl = `${crmUrl}/servicetemplates/${houseId}`;
    const editVisitTemplatesLink = (
      <a
        className={linkClassName}
        href={visitTemplatesUrl}
        rel="noopener noreferrer"
        style={crmLinkStyle}
        target="_blank">
        {linkLabel}
      </a>
    );

    let editVisitTemplateLink;
    if (destination.visitTemplate) {
      const {id: templateId} = destination.visitTemplate;
      editVisitTemplateLink = (
        <a
          className={linkClassName}
          href={`${visitTemplatesUrl}/template/${templateId}`}
          rel="noopener noreferrer"
          style={crmLinkStyle}
          target="_blank">
          {linkLabel}
        </a>
      );
    }

    return [editVisitTemplatesLink, editVisitTemplateLink];
  }

  get notesMapping() {
    return {
      importantNotes: {
        className: "warning",
        label: "Important Notes",
        onChange: this.handleChangeText.bind(this, "importantNotes"),
        value: this.state.importantNotes || "",
      },
      notes: {
        className: "info",
        label: "Notes",
        onChange: this.handleChangeText.bind(this, "notes"),
        value: this.state.notes || "",
      },
      schedulerNotes: {
        className: "scheduler",
        label: "Internal Scheduling Notes",
        onChange: this.handleChangeText.bind(this, "schedulerNotes"),
        value: this.state.schedulerNotes || "",
      },
    };
  }

  renderContent() {
    const {params: {id}, routeData} = this.context;
    const destination = getDestination(routeData, id);
    if (!destination) {
      return null;
    }

    const {modal} = this.props;

    const [
      crmEditVisitTemplatesLink,
      crmEditVisitTemplateLink,
    ] = this.getCRMLinks(destination);

    return (
      <div className="small">
        <div className={modal && "erg-margin-15"}>
          <DestinationTitle
            data={destination}
            style={{fontSize: styles.FONT_SIZE.LARGE}} /><br />
          <DestinationAddress crmLink data={destination} />
        </div>

        <hr className="erg-margin-bottom-15 erg-margin-top-15" />

        <div
          className={modal && "erg-padding-15 erg-no-padding-top"}
          style={modal && styles.scrollableSidebar(209)}>
          {destination.slot === 1 ?
            this.renderEstTimings(destination) :
            this.renderSlotTimings(destination)}

          {this.renderNotes("schedulerNotes")}
          {this.renderNotes("importantNotes", crmEditVisitTemplatesLink)}
          {this.renderNotes("notes", crmEditVisitTemplateLink)}

          {this.renderTasks(crmEditVisitTemplateLink)}
        </div>
      </div>
    );
  }

  renderDuration(labelClassName, controlClassName) {
    const {duration} = this.state;
    return [
      <label
        className={labelClassName}
        htmlFor="duration"
        key="label">
        Duration
      </label>,
      <input
        autoComplete="off"
        className={controlClassName}
        id="duration"
        key="input"
        onChange={this.handleChangeDuration}
        style={{width: "70px"}}
        type="number"
        value={duration.asMinutes()} />,
      " min",
    ];
  }

  renderEstStartTimeHourSelect(className) {
    const {estStartTime} = this.state;
    return (
      <select
        className={className}
        id="hour"
        onChange={this.handleChangeEstStartTime.bind(this, "hour")}
        value={estStartTime.hour()}>
        {I.Range(0, 24).map(item => (
          <option key={item} value={item}>
            {estStartTime.clone().hour(item).format("H A")}
          </option>
        )).toArray()}
      </select>
    );
  }

  renderEstStartTimeMinuteSelect(className) {
    const {estStartTime} = this.state;
    return (
      <select
        className={className}
        onChange={this.handleChangeEstStartTime.bind(this, "minute")}
        value={estStartTime.minute()}>
        {I.Range(0, 61).map(item => (
          <option key={item} value={item}>{item}</option>
        )).toArray()}
      </select>
    );
  }

  renderEstTimings() {
    const {duration, estStartTime} = this.state;

    const controlClassName = "form-control input-sm erg-margin-left-5";
    const staticControlClassName = "erg-inline-block erg-margin-left-5";

    const estEndTime = estStartTime.clone().add(duration);

    return (
      <div className="form-inline erg-margin-bottom-30 EditDestinationForm-Timings">
        <label className="control-label" htmlFor="hour">Start At</label>
        {this.renderEstStartTimeHourSelect(controlClassName)}
        {" :"}
        {this.renderEstStartTimeMinuteSelect(controlClassName)}
        <br className="visible-xs-block" />

        <label className="control-label Label">End At</label>
        <span
          className={staticControlClassName}
          style={{verticalAlign: "middle", width: "80px"}}>
          {estEndTime.format(TIME_FORMAT)}
        </span>
        <br className="visible-xs-block" />
        <br className="visible-xs-block" />

        {this.renderDuration("control-label", controlClassName)}
      </div>
    );
  }

  renderNotes(key, crmLink=null) {
    const {className, label, onChange, value} = this.notesMapping[key];
    return (
      <div className="row erg-margin-bottom-30">
        {this.renderRowHeader(label, crmLink)}
        <div className="col-xs-12 col-sm-6">
          <AutoTextarea
            className="form-control input-sm"
            onChange={onChange}
            value={value} />
        </div>
        <div className="col-sm-6 hidden-xs">
          <Markdown className={`alert alert-${className}`}>
            {value}
          </Markdown>
        </div>
      </div>
    );
  }

  renderRowHeader(label, crmLink=null, extraLink=null) {
    return (
      <h3
        className="clearfix col-xs-12 erg-no-margin erg-margin-bottom-5"
        style={{fontSize: styles.FONT_SIZE.LARGE}}>
        <span className="text-uppercase">{label}</span>
        {crmLink}
        {extraLink}
      </h3>
    );
  }

  renderSlotTimings(destination) {
    const {routeData} = this.context;
    const {estEndTime, estStartTime} = destination;

    const controlClassName = "form-control input-sm erg-margin-left-5";
    const staticControlClassName = "erg-inline-block erg-margin-left-5";

    let slotValueContent = `#${destination.slot}`;
    if (isDesktop() && canReorderDestinations(routeData)) {
      slotValueContent = (
        <a
          href="#"
          onClick={this.handleClickSlot}
          title="Change Destination Slot">
          {slotValueContent}
        </a>
      );
    }

    return (
      <div className="form-inline erg-margin-bottom-30 EditDestinationForm-Timings">
        <label className="control-label">Slot</label>
        <span
          className={staticControlClassName}
          style={{verticalAlign: "middle"}}>
          {slotValueContent}
        </span>
        <br className="visible-xs-block" />

        {this.renderDuration("control-label Label", controlClassName)}
        <br className="visible-xs-block" />
        <br className="visible-xs-block" />

        <label className="control-label Label">Estimated</label>
        <span
          className={staticControlClassName}
          style={{verticalAlign: "middle"}}>
          {estStartTime.format(TIME_FORMAT)}
          {" to "}
          {estEndTime.format(TIME_FORMAT)}
        </span>
      </div>
    );
  }

  renderTask(task) {
    const {description, id, title} = task;
    return (
      <div
        className="EditableTask col-xs-12 erg-margin-bottom-15 form-group"
        key={`task-${id}`}>
        <div className="input-group input-group-sm">
          <input
            className="form-control TaskTitle"
            onChange={this.handleChangeTask.bind(this, id, "title")}
            placeholder="Title"
            required
            value={title || ""} />
          <span
            className="input-group-addon"
            onClick={this.handleDeleteTask.bind(this, id)}>
            <i className="fa fa-times" />
          </span>
        </div>
        <AutoTextarea
          className="form-control input-sm TaskDescription"
          onChange={this.handleChangeTask.bind(this, id, "description")}
          placeholder="Description (Optional)"
          value={description || ""} />
      </div>
    );
  }

  renderTasks(crmLink=null) {
    const {crmLinkStyle} = this.props;
    const {reorderTasks, tasks} = this.state;

    let content;
    let reorderTasksLink;
    if (reorderTasks) {
      content = <ReorderTasks data={tasks} onMove={this.handleMoveTask} />;
      reorderTasksLink = (
        <small
          className="pull-right text-muted erg-margin-right-10"
          style={{
            height: "20px",
            fontSize: styles.FONT_SIZE.SMALL,
            lineHeight: "20px",
          }}>
          Drag and drop tasks to reorder
        </small>
      );
      crmLink = (
        <button
          className="NoBoxShadow btn btn-default btn-xs pull-right"
          onClick={this.handleDisableReorderTasks}
          style={{lineHeight: "18px", padding: "0 8px"}}>
          Done
        </button>
      );
    } else {
      content = [
        tasks.map(item => this.renderTask(item)),
        <div className="col-xs-12" key="add-task">
          <a
            className="erg-add-link"
            href="#"
            onClick={this.handleAddTask}>
            <i className="fa fa-plus-circle" />Add Task
          </a>
        </div>,
      ];
      reorderTasksLink = isDesktop() && tasks.count() > 1 ? (
        <a
          className="erg-add-link pull-right text-muted erg-margin-right-10"
          href="#"
          onClick={this.handleEnableReorderTasks}
          style={crmLinkStyle}>
          <i className="fa fa-arrows" />Reorder Tasks
        </a>
      ) : null;
    }

    return (
      <div className="row EditDestinationForm-Tasks">
        {this.renderRowHeader("Tasks", crmLink, reorderTasksLink)}
        {content}
      </div>
    );
  }

  render() {
    const {modal} = this.props;
    const {saved, submitted} = this.state;

    let props = {};
    if (modal) {
      let btnHelpText;
      if (submitted) {
        btnHelpText = "Saving…";
      } else if (saved) {
        btnHelpText = "Saved…";
      } else {
        btnHelpText = `Press ${isMac() ? "Cmd" : "Ctrl"}+S to save changes`;
      }

      props = {
        btnExtraClassName: "btn-success",
        btnExtraDisabled: submitted,
        btnExtraLabel: "Save & Close",
        btnHelpText,
        btnHelpTextClassName: "HelpText pull-left",
        onExtraSubmit: (evt) => this.handleSubmit(evt, true),
      };
    }

    return (
      <ModalForm
        btnSubmitDisabled={submitted}
        btnSubmitLabel="Save"
        modalBodyClassName="erg-no-padding"
        modalDialogClassName="ModalLarge"
        modalTitle={null}
        onSubmit={this.handleSubmit}
        pageTitle="Edit Destination"
        {...props}
        {...this.props}>
        {this.renderContent()}
      </ModalForm>
    );
  }
}



/** WEBPACK FOOTER **
 ** ./src/app/v2/components/responsive/EditDestinationForm.js
 **/